import { ViewChild } from '@angular/core';
import { Component, ElementRef, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from 'src/app/service/user.service';
import { UnlockUserRequest } from 'src/app/shared/models/request';
import { UnlockUserResult } from 'src/app/shared/models/UnlockUserResult';
import {
  SecurtyAnswerStatus,
  UserStatus,
} from 'src/app/shared/models/ValidatedUser';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ToastOptions } from 'src/app/core/components/toast/toast.component';
import { firstValueFrom } from 'rxjs';
import { LoginService } from 'src/app/service/api/login.service';
import { RecaptchaService } from 'src/app/service/recaptcha.service';

@Component({
  selector: 'app-unlock-account',
  template: `<div class="unlockAccountContent">
    <div>
      <h1 role="heading" aria-label="Unlock Account">Unlock Account</h1>
    </div>
    <div class="unlockAccount-container">
      <app-toast
        [message]="unlockToastMessage"
        [type]="toastEnumOptions.Error"
      ></app-toast>
      <div fxLayoutAlign="end center" class="top10">
        <strong>*Required</strong>
      </div>

      <form role="none" [formGroup]="unlockAccountForm" (ngSubmit)="onSubmit()">
        <ul role="none" class="">
          <li role="none">
            <div class="tw-w-full tw-flex tw-justify-center app-mb2">
              <div #recaptchaContainer></div>
            </div>
          </li>
          <li role="none" *ngIf="captchaErrorMessage">
            <span class="error-message">{{ captchaErrorMessage }}</span>
          </li>

          <div dir="ltr" class="float-right">
            <button
              mat-raised-button
              type="button"
              class="cancelButton"
              (click)="onCancel()"
              aria-label="Cancel"
            >
              <span class="mat-button-wrapper">
                <mat-icon aria-hidden="true">block</mat-icon>
                <strong>CANCEL</strong>
              </span>
            </button>
            <button
              mat-raised-button
              color="primary"
              type="submit"
              style="width: 90px"
              aria-label="Next"
            >
              <mat-icon aria-hidden="true">done</mat-icon>
              Next
            </button>
          </div>
        </ul>
      </form>
      <p *ngIf="showErrorToast">
        <app-toast
          [message]="errorToastMessage"
          [type]="toastEnumOptions.UserError"
        ></app-toast>
      </p>
      <span
        tabindex="0"
        aria-label="If you are unable to see the CAPTCHA image above, please contact Sedgwick Support Services at 8666477610."
      >
        If you are unable to see the CAPTCHA image(s) above, please contact
        Sedgwick Support Services at (866) 647-7610.
      </span>
    </div>
  </div> `,
  styles: [
    `
      .unlockAccountContent {
        margin: 40px;
      }

      .unlockAccount-toast {
        background-color: var(--dark-blue);
        color: white;
        max-width: 600px;
      }

      .unlockAccount-container {
        width: 80%;
        max-width: 630px;
        margin: 30px;
      }

      .cancelButton {
        background-color: Transparent;
        background-repeat: no-repeat;
        border: none;
        cursor: pointer;
        overflow: hidden;
        outline: none !important;
      }

      .unlockAccount-container * {
        box-sizing: border-box;
      }

      .mat-select-underline {
        border-bottom: 1px solid var(--dark-blue);
      }

      .validationMessage {
        color: var(--red);
        font-weight: bold;
        margin-left: 30%;
        font-size: smaller;
        margin-top: 1%;
      }
    `,
  ],
})
export class UnlockAccountComponent implements OnInit {
  /** static: true so the container exists during ngOnInit (reCAPTCHA init runs there). */
  @ViewChild('recaptchaContainer', { static: true })
  recaptchaContainer: ElementRef;

  username: string;
  unlockToastMessage: string =
    'Your account has been locked. Please complete the CAPTCHA to unlock your account.';
  errorToastMessage: string = 'The system could not validate your answers.';
  showErrorToast: boolean;
  toastEnumOptions = ToastOptions;
  unlockAccountForm: UntypedFormGroup;
  unlockRequest: UnlockUserRequest;
  submitPressed: boolean;
  captchaErrorMessage: string;
  resetQuestions: boolean;
  recaptchaToken: string = '';
  recaptchaWidgetId: number = null;
  recaptchaInitialized = false;
  recaptchaSiteKey: string = '';

  constructor(
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private utility: UtilityService,
    private loginService: LoginService,
    private recaptchaService: RecaptchaService
  ) {}

  ngOnInit(): void {
    this.username = this.userService.username;
    this.unlockAccountForm = this.buildForm();
    void this.initializeRecaptcha();
  }

  buildForm(): UntypedFormGroup {
    return this.fb.group({});
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }

  buildUnlockUserRequest(): UnlockUserRequest {
    return new UnlockUserRequest({
      username: this.username,
      recaptchaToken: this.recaptchaToken,
    });
  }

  handleResetQuestions(): void {
    this.resetQuestions = !this.resetQuestions;
  }

  handleSecurityResponse(result: UnlockUserResult): void {
    let answerResponse = result.verificationResponse.answerStatus;

    switch (answerResponse) {
      case SecurtyAnswerStatus.Success:
        this.userService.accountUnlocked = true;
        this.onCancel();
        break;
      case SecurtyAnswerStatus.Failed:
        this.showErrorToast = true;
        this.handleResetQuestions();
        this.resetRecaptcha();
        break;
      case SecurtyAnswerStatus.DisabledFailure:
        this.userService.accountDisabled = true;
        this.onCancel();
        break;
    }
  }

  setInvalidCaptcha(): void {
    this.resetRecaptcha();
    this.captchaErrorMessage =
      'Please complete the reCAPTCHA verification before continuing.';
  }

  onSubmit(): void {
    this.submitPressed = true;
    this.captchaErrorMessage = '';

    const tokenFromWidget = this.recaptchaService.getResponse(
      this.recaptchaWidgetId
    );
    if (!this.utility.isNullOrEmpty(tokenFromWidget)) {
      this.recaptchaToken = tokenFromWidget;
    }

    if (this.utility.isNullOrEmpty(this.recaptchaToken)) {
      this.setInvalidCaptcha();
      return;
    }

    this.unlockRequest = this.buildUnlockUserRequest();

    this.userService.unlockUserAccount(this.unlockRequest).subscribe(
      (result) => {
        if (result.userStatus == UserStatus.BadCaptCha) {
          this.setInvalidCaptcha();
        } else {
          this.handleSecurityResponse(result);
        }
      },
      (e) => {
        // Currently returning BadResponse from API if invalid captcha, do we need something
        // better to differentiate between server errors and invalid captchas?
        this.setInvalidCaptcha();
      }
    );
  }

  private async initializeRecaptcha(): Promise<void> {
    if (this.recaptchaInitialized) {
      return;
    }

    try {
      const { siteKey } = await firstValueFrom(
        this.loginService.getRecaptchaSiteKey()
      );
      this.recaptchaSiteKey = siteKey;
      await this.recaptchaService.ensureLoaded();
      if (this.recaptchaContainer?.nativeElement && this.recaptchaWidgetId === null) {
        const widgetId = this.recaptchaService.render(
          this.recaptchaContainer.nativeElement,
          this.recaptchaSiteKey,
          'UNLOCK_ACCOUNT',
          {
            onSuccess: (token) => {
              this.recaptchaToken = token;
              this.captchaErrorMessage = '';
            },
            onExpired: () => {
              this.recaptchaToken = '';
            },
            onError: () => {
              this.recaptchaToken = '';
            },
          }
        );
        if (widgetId !== null) {
          this.recaptchaWidgetId = widgetId;
          this.recaptchaInitialized = true;
        }
      }
    } catch (error) {
      console.error('Unable to load reCAPTCHA.', error);
    }
  }

  private resetRecaptcha(): void {
    this.recaptchaToken = '';
    this.recaptchaService.reset(this.recaptchaWidgetId);
  }
}
